// FILE: useThreadEnvironment.ts
// Purpose: Resolve the environment a thread runs in, and the pull request it is
//          currently trying, for the header badges.
// Layer: Web hooks
//
// The thread carries only an `environmentId`; whether that id is somebody's
// personal workspace or the project's shared `qa` is a fact about the
// environment, and the badge has to say which — "Tu entorno" on a thread
// running in the team's QA would be a lie about whose data is at risk.
//
// Cached per project and shared by every open thread of it, because the answer
// changes about as often as an administrator creates an environment.

import type { EnvironmentId, ProjectId } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";

import { type AdminEnvironmentRow, EnvironmentsClient } from "../auth/EnvironmentsClient";
import { environmentQueryKeys } from "../lib/environmentQueries";

const ENVIRONMENTS_STALE_TIME_MS = 60_000;

export function projectEnvironmentsQueryOptions(projectId: ProjectId | null) {
  return {
    queryKey: environmentQueryKeys.list(projectId, "usable"),
    queryFn: async (): Promise<ReadonlyArray<AdminEnvironmentRow>> => {
      if (projectId === null) return [];
      return EnvironmentsClient.listUsable(projectId);
    },
    enabled: projectId !== null,
    staleTime: ENVIRONMENTS_STALE_TIME_MS,
    // A 403 is an ordinary answer here — no `p.env.use` on this project — and
    // retrying it just delays the badge falling back to nothing.
    retry: false,
  };
}

/**
 * The badge label for a thread's environment, or `null` when it has none (or
 * when the viewer may not read the project's environments, which reads the
 * same: no badge rather than a wrong one).
 */
export function useThreadEnvironmentLabel(input: {
  readonly projectId: ProjectId | null;
  readonly environmentId: EnvironmentId | null;
}): string | null {
  return useThreadEnvironmentBadges(input).environmentLabel;
}

/**
 * The badges the chat header shows for a thread's environment.
 *
 * Two, not one, and separate on purpose. "Entorno del proyecto: qa" says whose
 * data is at risk and does not change; "Probando PR #131" says what the code
 * currently *is* and lasts an afternoon. Folding the second into the first would
 * make the header's most stable label the one that changes most often, and a
 * person who read it an hour ago would trust a badge that has since become
 * wrong.
 *
 * Only for project environments: a personal one is never in a trial, because
 * `qa_try_pr` refuses to run on it.
 */
export function useThreadEnvironmentBadges(input: {
  readonly projectId: ProjectId | null;
  readonly environmentId: EnvironmentId | null;
}): {
  readonly environmentLabel: string | null;
  /** «Probando PR #131», or null when nothing is being tried here. */
  readonly trialLabel: string | null;
} {
  const query = useQuery(
    projectEnvironmentsQueryOptions(input.environmentId === null ? null : input.projectId),
  );
  if (input.environmentId === null) return { environmentLabel: null, trialLabel: null };
  const found = (query.data ?? []).find((row) => row.id === input.environmentId);
  if (found === undefined) return { environmentLabel: null, trialLabel: null };
  return {
    environmentLabel:
      found.kind === "project"
        ? `Entorno del proyecto: ${found.name ?? "sin nombre"}`
        : "Tu entorno",
    trialLabel: found.activeTrial === null ? null : `Probando PR #${found.activeTrial.prNumber}`,
  };
}
